import { useEffect, useState } from "react";
import { api, authedFileUrl } from "../api";
import { Download } from "lucide-react";

export default function AuditTrail() {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    // Build a synthetic audit trail from the data we have, newest first
    Promise.all([
      api.get("/drivers").then((r) => r.data),
      api.get("/trips").then((r) => r.data),
      api.get("/pay-statements").then((r) => r.data),
    ]).then(([drivers, trips, statements]) => {
      const items = [];
      drivers.forEach((d) =>
        items.push({ ts: d.created_at, user: "admin", role: "admin", action: "Created", entity: "Driver",
                     record: `${d.display_id} — ${d.name}`, summary: `Pay mode ${d.pay_type}` })
      );
      trips.forEach((t) =>
        items.push({ ts: t.created_at, user: "admin", role: "admin", action: "Created", entity: "Trip",
                     record: t.trip_number, summary: `${t.paid_miles} mi · ${t.origin_city}, ${t.origin_state} → ${t.destination_city}, ${t.destination_state}` })
      );
      statements.forEach((s) => {
        items.push({ ts: s.created_at, user: "admin", role: "admin", action: "Created", entity: "Pay Statement",
                     record: s.statement_number, summary: `Net $${s.totals.net.toFixed(2)}`, statement_id: s.statement_id });
        if ((s.status || "draft") === "paid") {
          items.push({ ts: s.created_at, user: "admin", role: "admin", action: "Marked paid", entity: "Pay Statement",
                       record: s.statement_number, summary: `Pay date ${s.pay_date || s.created_at.slice(0, 10)}`, statement_id: s.statement_id });
        }
      });
      items.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
      setEntries(items);
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Audit Trail</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Every create, edit, delete, export and sign-in is recorded with a change summary.
        </p>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="eyebrow">{entries.length} entries</div>
          <div className="text-xs text-slate-500">Newest first</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5">Timestamp</th>
                <th className="px-4 py-2.5">User</th>
                <th className="px-4 py-2.5">Action</th>
                <th className="px-4 py-2.5">Entity</th>
                <th className="px-4 py-2.5">Record</th>
                <th className="px-4 py-2.5">Change Summary</th>
                <th className="px-4 py-2.5 w-24 text-right">Statement</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No entries yet.</td></tr>}
              {entries.map((e, i) => (
                <tr key={e.audit_id || e.ts || `audit-${i}`} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{e.ts ? new Date(e.ts).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2.5">
                    {e.user} <span className="pill-blue ml-1">{e.role}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={e.action === "Created" ? "pill-green" : e.action === "Deleted" ? "pill-red" : "pill-amber"}>{e.action}</span>
                  </td>
                  <td className="px-4 py-2.5">{e.entity}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{e.record}</td>
                  <td className="px-4 py-2.5 text-slate-600">{e.summary}</td>
                  <td className="px-4 py-2.5 text-right">
                    {e.statement_id ? (
                      <a
                        href={authedFileUrl(`/pay-statements/${e.statement_id}/pdf`)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-semibold"
                        data-testid={`audit-download-${e.statement_id}`}
                        title="Download pay statement PDF"
                      >
                        <Download size={14} /> PDF
                      </a>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
